import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import MenuItem from "@mui/material/MenuItem";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import TextInput from "@mui/material/TextField";

import {
	BooleanField,
	CalculationField,
	CompetenceListField,
	DateField,
	DecimalField,
	Field,
	FieldType,
	GroupField,
	ImageField,
	IntegerField,
	MultiSelectField,
	SelectField,
	TextField,
	UploadField,
	UrlField
} from "core/field/FieldService";
import { textFieldCellStyle } from "style/table/TableStyles";
import CreateTextField from "utils/edit/CreateTextField";

export type NewFormFieldRowProps = {
	supportedTypes: FieldType[];

	onCreate: (field: Field) => void;
};

let lastUsedType: FieldType | undefined;

const NewFormFieldRow: React.FC<NewFormFieldRowProps> = (
	props: NewFormFieldRowProps
) => {
	const { t } = useTranslation();

	const [type, setType] = useState(lastUsedType ?? props.supportedTypes[0]);

	function fieldTypeFromValue(value: string): FieldType {
		return (
			props.supportedTypes.find(key => key === value) ??
			props.supportedTypes[0]
		);
	}

	function createField(name: string): Field {
		switch (type) {
			case FieldType.BOOLEAN:
				return new BooleanField(name);
			case FieldType.CALCULATION:
				return new CalculationField(name);
			case FieldType.COMPETENCE_LIST:
				return new CompetenceListField(name);
			case FieldType.DATE:
				return new DateField(name);
			case FieldType.DECIMAL:
				return new DecimalField(name);
			case FieldType.GROUP:
				return new GroupField(name);
			case FieldType.INTEGER:
				return new IntegerField(name);
			case FieldType.IMAGE:
				return new ImageField(name);
			case FieldType.SELECT:
				return new SelectField(name);
			case FieldType.MULTI_SELECT:
				return new MultiSelectField(name);
			case FieldType.TEXT:
				return new TextField(name);
			case FieldType.URL:
				return new UrlField(name);
			case FieldType.UPLOAD:
				return new UploadField(name);
			default:
				throw new Error("Unknown field type: " + type);
		}
	}

	function onCreateField(name: string): void {
		if (name === "") {
			return;
		}

		lastUsedType = type;
		props.onCreate(createField(name.replace(".", "")));
	}

	return (
		<TableRow>
			<TableCell />
			<TableCell />
			<TableCell />
			<TableCell
				sx={{
					...textFieldCellStyle(true, true),
					width: "20ch"
				}}>
				<TextInput
					id="type-select"
					select
					variant="standard"
					fullWidth
					value={type}
					onChange={event =>
						setType(fieldTypeFromValue(event.target.value))
					}>
					{props.supportedTypes.map(type => {
						return (
							<MenuItem key={type} value={type}>
								{t(type)}
							</MenuItem>
						);
					})}
				</TextInput>
			</TableCell>
			<TableCell sx={textFieldCellStyle(true, true)}>
				<CreateTextField
					id="name-field"
					variant="standard"
					fullWidth
					onCreate={onCreateField}
				/>
			</TableCell>
		</TableRow>
	);
};

export default NewFormFieldRow;
